import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Download, Trash2, Shield, FileText, Award, Clock,
  CheckCircle2, AlertTriangle, BookOpen, Target, TrendingUp, BarChart3,
} from 'lucide-react';

const TranscriptPage = () => {
  const { api, user } = useAuth();
  const [transcript, setTranscript] = useState(null);
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        api.get('/compliance/transcript'),
        api.get('/compliance/consent/status'),
      ]);
      setTranscript(tRes.data);
      setConsents(cRes.data.consents || {});
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/compliance/gdpr/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my_data_export.json';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleDeleteRequest = async () => {
    if (deleteConfirm !== 'DELETE MY ACCOUNT') return;
    try {
      await api.delete('/compliance/gdpr/delete');
      toast.success('Account deletion processed');
      window.location.href = '/';
    } catch { toast.error('Deletion failed'); }
  };

  const cpd = transcript?.cpd;
  const currentYear = new Date().getFullYear();

  // Filter transcript by year
  const filteredTranscript = transcript?.transcript?.filter(entry => {
    if (yearFilter === 'all') return true;
    const year = yearFilter;
    const completedAt = entry.completed_at || entry.enrolled_at || '';
    return completedAt.startsWith(year);
  }) || [];

  // Available years from transcript
  const availableYears = [...new Set(
    (transcript?.transcript || [])
      .map(e => (e.completed_at || e.enrolled_at || '').substring(0, 4))
      .filter(y => y && y.length === 4)
  )].sort((a, b) => b - a);

  if (loading) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Clock className="h-8 w-8 animate-spin text-[#20acf6]" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="transcript-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2a3e5d]" data-testid="transcript-heading">My Learning Record</h1>
        <p className="text-slate-600 mt-1">Your complete learning transcript, CPD hours, certificates, and data rights</p>
      </div>

      {/* Stats Row */}
      {transcript && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 text-center">
            <BookOpen className="h-6 w-6 text-[#20acf6] mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2a3e5d]">{transcript.total_courses_enrolled}</p>
            <p className="text-xs text-slate-500">Courses Enrolled</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2a3e5d]">{transcript.total_courses_completed}</p>
            <p className="text-xs text-slate-500">Completed</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 text-[#02d1d4] mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2a3e5d]">{transcript.total_learning_hours}h</p>
            <p className="text-xs text-slate-500">Total CPD Hours</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Award className="h-6 w-6 text-amber-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2a3e5d]">{transcript.total_certificates}</p>
            <p className="text-xs text-slate-500">Certificates</p>
          </CardContent></Card>
        </div>
      )}

      {/* CPD Hours Breakdown */}
      {cpd && (
        <Card data-testid="cpd-breakdown">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-[#02d1d4]" /> CPD Hours Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Hours This Year */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">CPD Hours ({currentYear})</span>
                  <span className="text-lg font-bold text-[#2a3e5d]">{cpd.hours_this_year}h</span>
                </div>
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${cpd.hours_this_year >= 20 ? 'bg-green-500' : cpd.hours_this_year > 0 ? 'bg-[#20acf6]' : 'bg-slate-200'}`}
                    style={{ width: `${Math.min(100, (cpd.hours_this_year / 20) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {cpd.hours_this_year >= 20
                    ? <span className="text-green-600 font-medium">Annual target met!</span>
                    : cpd.hours_this_year > 0
                      ? `${Math.max(0, 20 - cpd.hours_this_year)}h remaining to reach 20h target`
                      : 'Complete courses to start logging CPD hours'
                  }
                </p>
              </div>

              {/* By Category */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Hours by Category</p>
                <div className="space-y-1.5">
                  {Object.entries(cpd.by_category).length > 0 ? Object.entries(cpd.by_category).map(([cat, hours]) => (
                    <div key={cat} className="flex items-center gap-2" data-testid={`cpd-cat-${cat}`}>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#20acf6] shrink-0" />
                        <span className="text-sm text-slate-600 truncate">{cat}</span>
                      </div>
                      <span className="text-sm font-semibold text-[#2a3e5d]">{hours}h</span>
                    </div>
                  )) : (
                    <p className="text-xs text-slate-400 italic">No CPD hours recorded yet. Complete a course to get started.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Year Breakdown */}
            {Object.keys(cpd.by_year).length > 1 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-2">Annual Breakdown</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(cpd.by_year).map(([year, hours]) => (
                    <div key={year} className="px-3 py-1.5 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-slate-500">{year}</p>
                      <p className="text-sm font-bold text-[#2a3e5d]">{hours}h</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transcript Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Learning Transcript</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="year-filter">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTranscript.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Course</th>
                    <th className="pb-2 pr-4">Category</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2 pr-4">CPD Hours</th>
                    <th className="pb-2 pr-4">Score</th>
                    <th className="pb-2 pr-4">Certificate</th>
                    <th className="pb-2">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTranscript.map((entry, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-medium text-[#2a3e5d]">{entry.course_title}</td>
                      <td className="py-3 pr-4 text-slate-600">{entry.category || '-'}</td>
                      <td className="py-3 pr-4">
                        <Badge className={entry.status === 'completed' ? 'bg-green-100 text-green-700' : entry.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                          {entry.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        {entry.status === 'completed' && entry.duration_hours > 0
                          ? <span className="font-medium text-[#02d1d4]">{entry.duration_hours}h</span>
                          : <span className="text-slate-400">-</span>
                        }
                      </td>
                      <td className="py-3 pr-4">{entry.best_quiz_score != null ? `${entry.best_quiz_score}%` : '-'}</td>
                      <td className="py-3 pr-4">{entry.certificate_number || '-'}</td>
                      <td className="py-3">{entry.completed_at ? entry.completed_at.split('T')[0] : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No courses enrolled yet. Browse courses to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* GDPR & Data Rights */}
      <Card data-testid="data-rights-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#20acf6]" /> Your Data Rights (UK GDPR)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2" data-testid="export-data-btn">
              <Download className="h-4 w-4" /> {exporting ? 'Exporting...' : 'Export My Data'}
            </Button>
            <Button variant="outline" onClick={() => setShowDeleteDialog(true)} className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-account-btn">
              <Trash2 className="h-4 w-4" /> Request Account Deletion
            </Button>
          </div>

          {/* Consent Status */}
          <div className="text-sm space-y-1">
            <p className="font-medium text-slate-700">Consent Status:</p>
            {Object.entries(consents).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2 text-slate-600">
                {val ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                <span className="capitalize">{key.replace('_', ' ')}: {val ? 'Granted' : 'Not granted'}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Your Account</DialogTitle>
            <DialogDescription>
              This action is irreversible. All your data, progress, and certificates will be permanently deleted (subject to legal retention requirements).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Type <strong>DELETE MY ACCOUNT</strong> to confirm:</p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full border p-2 rounded text-sm"
              placeholder="Type DELETE MY ACCOUNT"
            />
            <div className="flex gap-2">
              <Button onClick={() => setShowDeleteDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleDeleteRequest} disabled={deleteConfirm !== 'DELETE MY ACCOUNT'} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                Permanently Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranscriptPage;
